import { useGLTF } from "@react-three/drei";
import { useMemo, useEffect } from "react";
import * as THREE from "three";
import GlbLoader from "../../Utils/GlbLoader";
import { assignMaterialToObjects } from "../../Utils/AssignMaterialToObject";
import { stateMemory, stateContentReady } from "../../store";
import { useSnapshot } from "valtio";

export default function MDRRoomShell({ visible }) {
  const { imperfectionRoughMap, blackGlasMaterial } = GlbLoader();
  const snapStateMemory = useSnapshot(stateMemory);
  const contentReady = useSnapshot(stateContentReady)[0];
  const gltf = useGLTF("../models/Museum01/MuseenDerRegion.glb");

  const mats = useMemo(() => {
    const m = {
      partsBlackGlossyWallMaterial:  new THREE.MeshStandardMaterial({ roughness: 0.2, color: "#181818", metalness: 0, roughnessMap: imperfectionRoughMap, aoMapIntensity: 1.2 }),
      partsOrangeGlossy:             new THREE.MeshStandardMaterial({ roughness: 0, metalness: 0, color: "#fc8207", transparent: true, opacity: 1 }),
      floorBlackMattWallMaterial:    new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#181818", metalness: 0, roughnessMap: imperfectionRoughMap }),
      wallBlackMattWallMaterial:     new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#3E3E3E", metalness: 0, roughnessMap: imperfectionRoughMap, emissive: "#424242", aoMapIntensity: 1, emissiveIntensity: 0.4 }),
      partsBlackMattWallMaterial:    new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#181818", metalness: 0, roughnessMap: imperfectionRoughMap, aoMapIntensity: 1.2 }),
      GlasWindowsMaterial:           new THREE.MeshStandardMaterial({ color: 0x000000, roughness: 0.1, transparent: true, opacity: 0.5 }),
      panoMaterial:                  new THREE.MeshStandardMaterial({ roughness: 0.75, metalness: 0, color: "#1a1a1a" }),
    };

    assignMaterialToObjects(gltf.scene, ["rand"], m.partsBlackGlossyWallMaterial);
    assignMaterialToObjects(gltf.scene, ["Orange_Models"], m.partsOrangeGlossy);
    assignMaterialToObjects(gltf.scene, ["Floor", "Floor_right", "backWall"], m.floorBlackMattWallMaterial);
    assignMaterialToObjects(gltf.scene, ["Wall"], m.wallBlackMattWallMaterial);
    assignMaterialToObjects(gltf.scene, ["Decke"], m.partsBlackMattWallMaterial);
    assignMaterialToObjects(gltf.scene, ["FensterGlas", "Balkon"], m.GlasWindowsMaterial);
    assignMaterialToObjects(gltf.scene, ["Balkon"], blackGlasMaterial);
    assignMaterialToObjects(gltf.scene, ["Sphere004"], m.panoMaterial);

    return m;
  }, [gltf.scene]);

  useEffect(() => {
    if (!visible || !contentReady) return;

    const base = snapStateMemory.memoryLowDevice
      ? "../overallTextures/MDR/LOW/"
      : "../overallTextures/MDR/";

    let active = true;
    const loadedTextures = [];

    const track = (tex) => { loadedTextures.push(tex); return tex; };

    const loader = new THREE.TextureLoader();

    loader.load(base + "PanoHallstatt.jpg", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace;
      track(tex);
      mats.panoMaterial.color.set(0xffffff);
      mats.panoMaterial.map = tex; mats.panoMaterial.needsUpdate = true;
    });
    loader.load(base + "MDR_floor_AO.jpg", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace;
      track(tex);
      mats.floorBlackMattWallMaterial.aoMap = tex; mats.floorBlackMattWallMaterial.needsUpdate = true;
    });
    loader.load(base + "MDR_parts_AO.jpg", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace;
      track(tex);
      [mats.partsOrangeGlossy, mats.partsBlackMattWallMaterial, mats.partsBlackGlossyWallMaterial].forEach(m => {
        m.aoMap = tex; m.needsUpdate = true;
      });
    });
    loader.load(base + "MDR_walls_AO.jpg", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace;
      track(tex);
      mats.wallBlackMattWallMaterial.aoMap = tex;
      mats.wallBlackMattWallMaterial.emissiveMap = tex;
      mats.wallBlackMattWallMaterial.needsUpdate = true;
    });

    return () => {
      active = false;
      loadedTextures.forEach(tex => tex.dispose());
      mats.panoMaterial.map = null; mats.panoMaterial.needsUpdate = true;
      mats.floorBlackMattWallMaterial.aoMap = null; mats.floorBlackMattWallMaterial.needsUpdate = true;
      [mats.partsOrangeGlossy, mats.partsBlackMattWallMaterial, mats.partsBlackGlossyWallMaterial].forEach(m => {
        m.aoMap = null; m.needsUpdate = true;
      });
      mats.wallBlackMattWallMaterial.aoMap = null;
      mats.wallBlackMattWallMaterial.emissiveMap = null;
      mats.wallBlackMattWallMaterial.needsUpdate = true;
    };
  }, [visible, contentReady]);

  return <primitive object={gltf.scene} visible={visible} />;
}
